/* Repository path: frontend/src/features/estimates/ProfileChangeSummary.tsx */

import { useEffect, useRef } from "react";
import type { PlanItem, ProfileEditResult } from "../../types";
import { Button } from "../../components/Button";

/**
 * W4-07 — the post-save "what changed" summary. Rendered by whichever
 * page owns the plan (`ResultPage` for a preview, `SavedPlanPage` for a
 * saved plan) as the `changeSummary` node `EstimateView` accepts, exactly
 * once, right after a successful profile edit. It is the result of an
 * action, not a property of the plan: the caller clears its `result` on
 * the next navigation, so it never survives a reload.
 *
 * One plain sentence per machine reason code, with a cautious default for
 * anything unrecognised — never "deleted", "lost" or "reset", and never a
 * claim that a figure improved or worsened.
 */
const REASON_SENTENCES: Record<string, string> = {
  superseded_by_profile: "Your new answers already tell us this.",
  profile_dependency_changed: "This answer was about your previous situation.",
  no_longer_needed: "Nothing in your plan needs this any more.",
  invalid_after_edit: "This answer no longer fits your new answers.",
  dependency_invalidated: "This depended on an answer we now need to ask again.",
};
const FALLBACK_REASON_SENTENCE = "We need to check this one again.";

/**
 * Item names, where they can be honestly shown, and a count of the rest.
 * A `removed`/`dormant` code cannot be named — the item is no longer in
 * `items` at all, and this codebase deliberately keeps no local
 * code-to-label map (W4-01) — so those are always reported as a count.
 * `added`/`changed` codes usually resolve against the *current* `items`,
 * mirroring exactly how `refinementCopy.ts`'s `changedItemsSentence`
 * already treats an unresolvable code: still counted, never silently
 * dropped, just not named.
 */
function namesAndCount(codes: string[], items: PlanItem[]): { named: string[]; unnamed: number } {
  const byCode = new Map(items.map((item) => [item.code, item.english_name]));
  const named: string[] = [];
  let unnamed = 0;
  for (const code of codes) {
    const name = byCode.get(code);
    if (name) named.push(name);
    else unnamed += 1;
  }
  return { named, unnamed };
}

function itemListSentence(label: string, codes: string[], items: PlanItem[]): string | null {
  if (codes.length === 0) return null;
  const { named, unnamed } = namesAndCount(codes, items);
  const parts = [...named];
  if (unnamed > 0) parts.push(unnamed === 1 ? "one more item" : `${unnamed} more items`);
  return `${label}: ${parts.join(", ")}.`;
}

function dormantLabel(dormant: { actuals: string[]; deadlines: string[] }): string {
  const codes = new Set([...dormant.actuals, ...dormant.deadlines]);
  return codes.size === 1 ? "1 item" : `${codes.size} items`;
}

export function ProfileChangeSummary({
  result,
  items,
  onImprove,
  onDismiss,
}: {
  result: ProfileEditResult | null;
  items: PlanItem[];
  /** Opens the existing `Improve my estimates` flow — the profile editor
   * never asks a re-ask question itself. */
  onImprove?: () => void;
  onDismiss?: () => void;
}) {
  const heading = useRef<HTMLHeadingElement>(null);
  // W4-07: focus moves here once, right after a save — the same
  // return-and-focus discipline `Confirmation` and W4-05's plan return
  // already follow. Runs once per mount; a fresh `ProfileChangeSummary`
  // is mounted only when a new `result` object is handed down. No
  // eslint-disable needed here — the effect only touches a ref, which
  // react-hooks/exhaustive-deps already excludes from the dependency
  // check.
  useEffect(() => { heading.current?.focus(); }, []);

  if (!result) return null;

  if (!result.material_change) {
    return (
      <section className="confirmation profile-change-summary" role="status" aria-labelledby="profile-change-heading">
        <p className="eyebrow">Your answers</p>
        <h2 id="profile-change-heading" tabIndex={-1} ref={heading}>
          Your answers are the same as before, so your plan hasn’t changed.
        </h2>
        {onDismiss && <Button variant="quiet" onClick={onDismiss}>Dismiss</Button>}
      </section>
    );
  }

  // Only named-input codes that are *currently* the reported reason an
  // item needs more information are shown as something to re-ask — a
  // code invalidated for `superseded_by_profile` or `no_longer_needed`
  // has nothing left to ask about, and saying otherwise would be a
  // promise this plan cannot keep.
  const missingInputLabels = new Map(
    items.flatMap((item) =>
      item.missing_input ? ([[item.missing_input.code, item.missing_input.label]] as const) : [],
    ),
  );
  const reAskEntries = Object.entries(result.invalidation_reasons).filter(([code]) =>
    missingInputLabels.has(code),
  );

  const changedAnswersLine = result.changed_profile_fields.length > 0
    ? `You changed ${result.changed_profile_fields.length === 1 ? "one answer" : `${result.changed_profile_fields.length} answers`}.`
    : null;
  const addedLine = itemListSentence("Now in your plan", result.added_item_codes, items);
  const changedLine = itemListSentence("Amounts that moved", result.changed_item_codes, items);
  const removedCount = result.removed_item_codes.length;
  const hasDormant = result.dormant_records.actuals.length > 0
    || result.dormant_records.deadlines.length > 0;

  return (
    <section className="confirmation profile-change-summary" role="status" aria-labelledby="profile-change-heading">
      <p className="eyebrow">Your answers</p>
      <h2 id="profile-change-heading" tabIndex={-1} ref={heading}>What changed</h2>
      {changedAnswersLine && <p>{changedAnswersLine}</p>}
      {addedLine && <p>{addedLine}</p>}
      {removedCount > 0 && (
        <p>
          {removedCount === 1
            ? "One item is no longer part of your plan."
            : `${removedCount} items are no longer part of your plan.`}
        </p>
      )}
      {changedLine && <p>{changedLine}</p>}
      {result.estimate_period_changed && (
        <p>Your plan period is now different, to match your new dates.</p>
      )}
      {reAskEntries.length > 0 && (
        <div className="profile-change-summary__reask">
          <p className="profile-change-summary__eyebrow">We need to ask you again about:</p>
          <ul>
            {reAskEntries.map(([code, reason]) => (
              <li key={code}>
                {missingInputLabels.get(code)} — {REASON_SENTENCES[reason] ?? FALLBACK_REASON_SENTENCE}
              </li>
            ))}
          </ul>
          {onImprove && <Button variant="secondary" onClick={onImprove}>Improve my estimates</Button>}
        </div>
      )}
      {hasDormant && (
        <p>
          We’ve kept what you recorded for {dormantLabel(result.dormant_records)} that {
            new Set([...result.dormant_records.actuals, ...result.dormant_records.deadlines]).size === 1
              ? "isn’t"
              : "aren’t"
          } part of your plan right now. It will come back if this applies again.
        </p>
      )}
      {onDismiss && <Button variant="quiet" onClick={onDismiss}>Dismiss</Button>}
    </section>
  );
}
